import { t, useLocale } from "@/i18n/locale";
import { Github, type LucideIcon } from "lucide-react";
import AtoLogo from "../../components/shell/AtoLogo";
import { docsRoute } from "../docsLocales";
import { COPYRIGHT_HOLDER, LEGAL_LINKS } from "../legal/operator";

interface FooterLink {
  label: string;
  href: string;
  /** Opens in a new tab with rel="noopener noreferrer" instead of routing in-app. */
  external?: boolean;
  /** Optional leading icon, e.g. a brand mark for an external destination. */
  icon?: LucideIcon;
}

interface FooterGroup {
  label: string;
  links: FooterLink[];
}

const FOOTER_GROUPS: FooterGroup[] = [
  {
    label: "Documentation",
    links: [
      { label: "About", href: "/about/" },
      { label: "Overview", href: "/docs/" },
      { label: "Using apps", href: "/docs/using-apps/" },
      { label: "Library and imports", href: "/docs/library/" },
    ],
  },
  {
    label: "Policies",
    links: [
      { label: "Terms", href: LEGAL_LINKS.terms },
      { label: "Privacy", href: LEGAL_LINKS.privacy },
      { label: "Acceptable Use", href: LEGAL_LINKS.acceptableUse },
      { label: "IP Policy", href: LEGAL_LINKS.ipPolicy },
      { label: "Security", href: LEGAL_LINKS.security },
      { label: "Billing & refunds", href: LEGAL_LINKS.billing },
      { label: "All legal", href: LEGAL_LINKS.terms },
    ],
  },
  {
    // NOT "Company" — ato.run is a trade name operated by an individual, and
    // a "Company" heading reads as a claim that a company runs it.
    label: "Legal",
    links: [
      { label: "Contact", href: LEGAL_LINKS.contact },
      { label: "Legal Notice", href: LEGAL_LINKS.tokushoho },
    ],
  },
  {
    label: "External Links",
    links: [
      {
        label: "GitHub",
        href: "https://github.com/ato-run/ato",
        external: true,
        icon: Github,
      },
    ],
  },
];

export const GLOBAL_FOOTER_LINKS = FOOTER_GROUPS;

/**
 * Static docs pages pass their locale; the app follows the chosen UI language.
 */
function localizedHref(href: string, docsLocale?: string): string {
  if (href === "/about/" && docsLocale === "zh-CN") return "/zh-cn/about/";
  // Japanese legal pages exist for every English document; the reference
  // translation keeps the same slugs, so the footer follows the page language.
  if (docsLocale === "ja" && href.startsWith("/legal/")) return `/ja${href}`;
  if (!docsLocale || !href.startsWith("/docs/")) return href;
  return docsRoute(docsLocale, href.slice("/docs/".length).replace(/\/$/, ""));
}

export default function GlobalFooter({ docsLocale }: { docsLocale?: string } = {}) {
  const locale = useLocale();
  return (
    <footer className="global-footer">
      <div className="global-footer__brand">
        <a href="/" aria-label={t("Ato home")}>
          <AtoLogo size={22} />
        </a>
        <p>{t("A new medium for software.")}</p>
      </div>
      <nav className="global-footer__nav" aria-label={t("Footer")}>
        {FOOTER_GROUPS.map((group) => (
          <div key={group.label}>
            <h2>{t(group.label)}</h2>
            <ul>
              {group.links.map((link) => (
                <li key={`${link.href}:${t(link.label)}`}>
                  <a
                    href={localizedHref(link.href, docsLocale ?? locale)}
                    {...(link.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {link.icon ? <link.icon size={14} aria-hidden /> : null}
                    {t(link.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <p className="global-footer__copyright">
        © {new Date().getUTCFullYear()} {COPYRIGHT_HOLDER}
      </p>
    </footer>
  );
}
